import type { Theme } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import Input from '@material-ui/core/Input';
import InputAdornment from '@material-ui/core/InputAdornment';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import ClearOutlined from '@material-ui/icons/ClearOutlined';
import SearchOutlined from '@material-ui/icons/SearchOutlined';
import * as React from 'react';
import DialogTitle from 'v2/components/Shared/DialogTitle';
import { useCurrencies } from 'v2/hooks/useCurrencies';

import type { Currency } from 'v2/interfaces/currency';

import { useStyles } from './styles';

type ChooseCurrenciesModalProps = {
  isOpen: boolean;
  closeModal: () => void;
  selectedCurrencies: Currency[];
  addCurrencies: (currencies: Currency[]) => void;
};

function ChooseCurrenciesModal({ isOpen, closeModal, selectedCurrencies: selectedCurrenciesProp = [], addCurrencies }: ChooseCurrenciesModalProps) {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('xs'));
  const classes = useStyles();
  const [search, setSearch] = React.useState('');
  const currenciesQuery = useCurrencies();
  const [selectedCurrencies, setSelectedCurrencies] = React.useState<Currency[]>([]);

  function handleCurrencyClick(currency: Currency) {
    const selectedCurrenciesIds = selectedCurrencies.map((selectedCurrency) => selectedCurrency.id);
    if (selectedCurrenciesIds.includes(currency.id)) {
      setSelectedCurrencies(selectedCurrencies.filter((selectedCurrency) => selectedCurrency.id !== currency.id));
      return;
    }
    setSelectedCurrencies([...selectedCurrencies, currency]);
  }

  function handleAddButtonClick() {
    setSelectedCurrencies([]);
    addCurrencies(selectedCurrencies);
    closeModal();
    setTimeout(() => setSearch(''), 500);
  }

  function handleClose() {
    setSelectedCurrencies([]);
    closeModal();
    setTimeout(() => setSearch(''), 500);
  }

  const filteredCurrencies = React.useMemo(() => {
    const noBRLCurrencies = currenciesQuery.data?.filter((currency) => currency.code !== 'BRL');
    const notAlreadySelectedCurrencies = noBRLCurrencies?.filter((currency) => {
      const selectedCurrenciesIds = selectedCurrenciesProp.map((selectedCurrency) => selectedCurrency.id);
      return !selectedCurrenciesIds.includes(currency.id);
    });
    if (!search) return notAlreadySelectedCurrencies;
    return notAlreadySelectedCurrencies.filter((currency) => {
      const codeMatches = currency.code.toLowerCase().includes(search.toLowerCase());
      const nameMatches = currency.name.toLowerCase().includes(search.toLowerCase());
      return codeMatches || nameMatches;
    });
  }, [currenciesQuery.data, search, selectedCurrenciesProp]);

  return (
    <Dialog
      open={isOpen}
      fullWidth
      fullScreen={isMobile}
      maxWidth='sm'
      classes={{
        paper: !isMobile ? classes.paper : '',
      }}
    >
      <DialogTitle onClose={handleClose}>
        <Typography>
          <strong>Escolher moedas</strong>
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Input
          id='search-currencies'
          fullWidth
          startAdornment={
            <InputAdornment position='start'>
              <SearchOutlined />
            </InputAdornment>
          }
          endAdornment={
            <InputAdornment position='end'>
              {!!search && (
                <IconButton size='small' aria-label='Limpar busca' onClick={() => setSearch('')} edge='end' disabled={!search}>
                  <ClearOutlined />
                </IconButton>
              )}
            </InputAdornment>
          }
          placeholder='Buscar moedas'
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <List>
          {filteredCurrencies?.map((currency) => (
            <>
              <ListItem key={currency.id}>
                <ListItemIcon>
                  <Checkbox
                    id={`currency-${currency.code}`}
                    onClick={() => handleCurrencyClick(currency)}
                    checked={selectedCurrencies.includes(currency)}
                    size='small'
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <label htmlFor={`currency-${currency.code}`}>
                      {currency.code} ({currency.name})
                    </label>
                  }
                />
              </ListItem>
              <Divider />
            </>
          ))}
          {filteredCurrencies?.length === 0 && (
            <ListItem>
              <ListItemText style={{ textAlign: 'center' }}>Nenhuma moeda encontrada</ListItemText>
            </ListItem>
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button id='add-currencies-button' variant='contained' color='primary' onClick={handleAddButtonClick} disabled={!selectedCurrencies.length}>
          Adicionar moedas {!!selectedCurrencies.length && `(${selectedCurrencies.length})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export { ChooseCurrenciesModal };
